/**
 * Session Token Service
 *
 * Gestiona los tokens de sesión para validación de peticiones y protección anti-bot.
 * - Genera tokens al iniciar la aplicación
 * - Renueva automáticamente cada 25 minutos
 * - Valida el origen de las peticiones mediante fingerprinting
 * - Duración del token: 30 minutos
 */

import { generateFingerprint } from '../utils/fingerprint';

class SessionTokenService {
  constructor() {
    this.token = null
    this.fingerprint = null
    this.refreshTimer = null
    this.isInitialized = false

    // Configuración
    this.REFRESH_INTERVAL = 25 * 60 * 1000 // 25 minutos en ms
    this.TOKEN_DURATION = 30 * 60 * 1000 // 30 minutos en ms
    this.MAX_RETRIES = 3
    this.RETRY_DELAY = 5000 // 5 segundos
  }

  /**
   * Inicializa el servicio de session tokens
   * Debe llamarse al inicio de la aplicación
   */
  async initialize() {
    if (this.isInitialized) {
      console.warn('[SessionToken] Service already initialized')
      return
    }

    try {
      console.log('[SessionToken] Initializing...')

      // Generar fingerprint del navegador
      this.fingerprint = await generateFingerprint()
      console.log('[SessionToken] Fingerprint generated')

      // Solicitar token inicial (sin PoW)
      await this.requestToken();

      // Configurar renovación automática
      this.setupAutoRefresh()

      this.isInitialized = true
      console.log('[SessionToken] ✓ Initialized successfully')
    } catch (error) {
      console.error('[SessionToken] Failed to initialize:', error)
      // Reintentar después de un delay
      setTimeout(() => this.initialize(), this.RETRY_DELAY)
    }
  }

  /**
   * Espera a que el servicio esté inicializado
   * Útil para asegurar que el token esté disponible antes de hacer peticiones
   * @param {number} timeout - Tiempo máximo de espera en ms (default: 30s)
   * @returns {Promise<void>}
   */
  async waitForInitialization(timeout = 30000) {
    if (this.isInitialized) {
      return
    }

    const startTime = Date.now()

    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (this.isInitialized) {
          clearInterval(checkInterval)
          resolve()
        } else if (Date.now() - startTime > timeout) {
          clearInterval(checkInterval)
          reject(new Error('[SessionToken] Initialization timeout'))
        }
      }, 100)
    })
  }

  /**
   * Solicita un nuevo token al backend (simplificado - sin PoW)
   */
  async requestToken(retryCount = 0) {
    try {
      console.log('[SessionToken] Requesting new token...')

      // Solicitar token directamente con fingerprint
      const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}/middleware/session/token`;
      const tokenResponse = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fingerprint: this.fingerprint,
        }),
      })

      if (!tokenResponse.ok) {
        throw new Error(`Token request failed: ${tokenResponse.status}`)
      }

      const { sessionToken, expiresIn, expiresAt } = await tokenResponse.json()

      this.token = sessionToken

      console.log('[SessionToken] ✓ Token obtained successfully')
      console.log('[SessionToken] Expires in:', Math.floor(expiresIn / 60), 'minutes')
      console.log('[SessionToken] Expires at:', new Date(expiresAt).toLocaleString())

      return this.token
    } catch (error) {
      console.error('[SessionToken] Failed to request token:', error)

      // Reintentar si no se alcanzó el máximo
      if (retryCount < this.MAX_RETRIES) {
        console.log(`[SessionToken] Retrying... (${retryCount + 1}/${this.MAX_RETRIES})`)
        await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY))
        return this.requestToken(retryCount + 1)
      }

      throw error
    }
  }

  /**
   * Configura la renovación automática del token
   */
  setupAutoRefresh() {
    // Limpiar timer anterior si existe
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }

    // Renovar cada 25 minutos
    this.refreshTimer = setInterval(async () => {
      console.log('[SessionToken] Auto-refresh triggered')
      await this.refreshToken()
    }, this.REFRESH_INTERVAL)

    console.log('[SessionToken] Auto-refresh configured (every 25 minutes)')
  }

  /**
   * Renueva el token actual
   */
  async refreshToken() {
    if (!this.token) {
      console.warn('[SessionToken] No token to refresh, requesting new one')
      return this.requestToken()
    }

    try {
      console.log('[SessionToken] Refreshing token...')

      const refreshUrl = `${process.env.REACT_APP_BACKEND_URL}/middleware/session/token/refresh`
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': this.token,
          'X-Fingerprint': this.fingerprint,
        },
        body: JSON.stringify({}),
      })

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`)
      }

      const { sessionToken, expiresIn, expiresAt } = await response.json()

      this.token = sessionToken

      console.log('[SessionToken] ✓ Token refreshed successfully')
      console.log('[SessionToken] New token expires in:', Math.floor(expiresIn / 60), 'minutes')
      console.log('[SessionToken] New token expires at:', new Date(expiresAt).toLocaleString())

      return this.token
    } catch (error) {
      console.error('[SessionToken] Failed to refresh token:', error)

      // Si falla el refresh, solicitar nuevo token
      console.log('[SessionToken] Requesting new token after refresh failure')
      return this.requestToken()
    }
  }

  /**
   * Obtiene el token actual
   * @returns {string|null}
   */
  getToken() {
    return this.token
  }

  /**
   * Obtiene el fingerprint actual
   * @returns {string|null}
   */
  getFingerprint() {
    return this.fingerprint
  }

  /**
   * Limpia el token actual
   */
  clearToken() {
    console.log('[SessionToken] Clearing token')
    this.token = null

    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  /**
   * Destruye el servicio
   */
  destroy() {
    console.log('[SessionToken] Destroying service')
    this.clearToken()
    this.fingerprint = null
    this.isInitialized = false
  }

  /**
   * Verifica si el servicio está inicializado
   * @returns {boolean}
   */
  isReady() {
    return this.isInitialized && this.token !== null
  }
}

// Exportar instancia singleton
export const sessionTokenService = new SessionTokenService()

// Exportar clase para testing
export { SessionTokenService }
